import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Card from "../components/Card";

const ViewCategories = () => {
  const { category } = useParams();
  const navigate = useNavigate();

  const [categoryDetails, setCategoryDetails] = useState(null);
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  // separate errors so one API doesn't kill everything
  const [categoryError, setCategoryError] = useState("");
  const [productsError, setProductsError] = useState("");

  const getErrorMessage = (err, fallback = "Something went wrong.") => {
    // axios errors usually live here:
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback
    );
  };

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      setLoading(true);
      setCategoryError("");
      setProductsError("");
      setProducts([]);
      setCategoryDetails(null);

      try {
        // 1) Category (must succeed)
        const categoryResponse = await axios.get(
          `https://api.vyapakprint.com/api/categories/getCategoryById/${category}`,
        );

        if (!alive) return;
        setCategoryDetails(categoryResponse.data);

        // 2) Products (nice-to-have; can fail gracefully)
        try {
          const productsResponse = await axios.get(
            `https://api.vyapakprint.com/api/products/products/category/${category}`,
          );

          if (!alive) return;

          // handle if API returns {message: "..."} or [] etc.
          const data = productsResponse.data;
          const list = Array.isArray(data) ? data : data?.products || [];
          setProducts(list);
        } catch (err) {
          if (!alive) return;
          // If API says "No products found..." show that instead of generic error
          setProductsError(getErrorMessage(err, "No products found."));
          setProducts([]); // keep UI stable
        }
      } catch (err) {
        if (!alive) return;
        setCategoryError(
          getErrorMessage(err, "Failed to load category details."),
        );
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      alive = false;
    };
  }, [category]);

  if (loading) return <p className="p-4">Loading...</p>;

  // category missing is a real page-level error
  if (categoryError) return <p className="p-4 text-red-600">{categoryError}</p>;
  if (!categoryDetails) return <p className="p-4">Category not found.</p>;

  const { name, subcategory = [] } = categoryDetails;

  const handleSubcategoryClick = (subcatId, subcatName) => {
    navigate(`/subcategory/${subcatId}/${subcatName}`);
  };

  return (
    <div className="my-2">
      <div className="max-w-7xl mx-auto p-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{name}</h2>

        {subcategory.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
            {subcategory.map((subcat) => (
              <div
                key={subcat._id}
                className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer"
                onClick={() => handleSubcategoryClick(subcat._id, subcat.name)}
              >
                <img
                  src={subcat.imgPath}
                  alt={subcat.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {subcat.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Products section (only when no subcategories) */}
        {subcategory.length === 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Products</h3>

              {/* show non-blocking product error nicely */}
              {productsError && (
                <span className="text-sm text-gray-600">{productsError}</span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.length > 0 ? (
                products.map((product) => (
                  <Card
                    key={product._id}
                    id={product._id}
                    name={product.name}
                    category={product?.category?.name || name}
                    price={product?.priceTiers?.[0]?.price ?? 0}
                    quantity={product?.priceTiers?.[0]?.quantity ?? 0}
                    image={product?.images?.[0]}
                  />
                ))
              ) : (
                <p className="text-gray-600">
                  {productsError || "No products available in this category."}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViewCategories;
